/* ==========================================================================
   Labor Day Sale — shop the sale

   One axis, three panels. The visitor picks a car once; each sale collection
   answers for itself, in parallel, without a page load.

   Shape of the thing:
     - Every chip is a real <a> to a real filtered collection page. Clicking is
       intercepted; middle-click, cmd-click and no-JS all still navigate.
     - A chip click fans out to one Section Rendering request per panel, each
       with its own AbortController, so a slow panel never blocks the others.
     - Nothing is fetched on load. Liquid already server-rendered the first
       cards in every panel, which is the whole feature working before this
       file has run.

   The guard is ported verbatim from assets/dm-tagged-product-browser.js
   (lines 295-323) rather than rewritten. It is the piece that makes the
   feature safe, it has been in production on page.pre-owned, and rewriting a
   working safety check to make it prettier is how safety checks stop working.

   One deliberate fix on the way over: in the original, the append-busy flag is
   cleared inside a `finally` gated on the request token still being current.
   Clicking a chip while a Load More is in flight aborts that append, so the
   guard fails, the flag is never cleared, and Load More is dead until the page
   is reloaded. Here the flag is per panel and cleared unconditionally.
   ========================================================================== */

(function () {
  'use strict';

  if (window.customElements && customElements.get('lp-labor-day-shop')) return;

  var SECTION_RENDER_PARAM = 'section_id';

  /* ---------------------------------------------------------------------
     Tag helpers — ported verbatim, do not "improve"
     --------------------------------------------------------------------- */

  function parseTagList(value) {
    if (!value) return [];
    return value
      .toLowerCase()
      .split(/[\s,+]+/)
      .map(function (t) {
        return t.trim();
      })
      .filter(Boolean);
  }

  function isSubset(needle, haystack) {
    for (var i = 0; i < needle.length; i += 1) {
      if (haystack.indexOf(needle[i]) === -1) return false;
    }
    return true;
  }

  function tagsFromPathname(pathname) {
    /* /collections/<handle>/<tag(+tag)> -> ['tag','tag']. Tolerates a locale
       prefix such as /en-gb/collections/... */
    var decoded;
    try {
      decoded = decodeURIComponent(pathname);
    } catch (e) {
      decoded = pathname;
    }
    var idx = decoded.indexOf('/collections/');
    if (idx === -1) return [];
    var rest = decoded.slice(idx + '/collections/'.length).replace(/\/+$/, '');
    var parts = rest.split('/');
    if (parts.length < 2) return [];
    return parseTagList(parts[1]);
  }

  function withSectionId(url, sectionId) {
    var u = new URL(url, window.location.origin);
    u.searchParams.set(SECTION_RENDER_PARAM, sectionId);
    return u.toString();
  }

  function isPlainLeftClick(event) {
    return (
      event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
    );
  }

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function pushEvent(name, payload) {
    if (!name || isDesignMode()) return;
    var data = { event: name, page_type: 'labor_day_sale' };
    if (payload) {
      Object.keys(payload).forEach(function (k) {
        if (payload[k] !== undefined && payload[k] !== '') data[k] = payload[k];
      });
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  }

  /* ---------------------------------------------------------------------
     Element
     --------------------------------------------------------------------- */

  class LabordayShop extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.endpointId = this.getAttribute('data-endpoint') || 'lp-labor-day-shop-endpoint';
      this.labels = {
        parts: this.getAttribute('data-label-parts') || 'parts',
        loading: this.getAttribute('data-label-loading') || 'Loading products',
        loadError: this.getAttribute('data-label-load-error') || 'Could not load more — try again',
        answerPrefix: this.getAttribute('data-answer-prefix') || 'For your',
        answerAll: this.getAttribute('data-answer-all') || 'on sale across every category',
        answerSuffix: this.getAttribute('data-answer-suffix') || 'on sale for this car'
      };

      this.rail = this.querySelector('[data-ld-rail]');
      this.answerEl = this.querySelector('[data-ld-answer]');
      this.chips = Array.prototype.slice.call(this.querySelectorAll('[data-ld-chip]'));

      this.panels = Array.prototype.slice
        .call(this.querySelectorAll('[data-ld-panel]'))
        .map(this.readPanel, this);

      if (!this.panels.length) return;

      this.cache = new Map();
      this.activeChip = this.querySelector('[data-ld-chip][aria-current="true"]') || this.chips[0];
      this.activeExpected = [];

      this._onRailClick = this.onRailClick.bind(this);
      if (this.rail) this.rail.addEventListener('click', this._onRailClick);

      /* Category tabs. Every panel is still fetched when the car changes — the
         answer line has to stay true — but only one is on screen. Liquid leaves
         all of them visible, so with the script dead the categories read as a
         stacked list rather than as two thirds of the page missing. */
      this.tablist = this.querySelector('[data-ld-tabs]');
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-ld-tab]'));
      if (this.tabs.length) {
        this._onTabClick = this.onTabClick.bind(this);
        this._onTabKey = this.onTabKey.bind(this);
        if (this.tablist) {
          this.tablist.addEventListener('click', this._onTabClick);
          this.tablist.addEventListener('keydown', this._onTabKey);
        }
        this.selectTab(0, { silent: true });
      }

      this.panels.forEach(function (panel) {
        if (!panel.moreBtn) return;
        panel.onMore = this.onLoadMore.bind(this, panel);
        panel.moreBtn.addEventListener('click', panel.onMore);
      }, this);

      /* An offer card elsewhere on the page can open one category before it
         scrolls here (assets/lp-labor-day-sale.js, data-ld-open-tab). */
      this._onOpenCategory = this.onOpenCategory.bind(this);
      document.addEventListener('ld:open-category', this._onOpenCategory);

      /* A car may already be chosen — a shared link, or a back-navigation. */
      this.restoreFromUrl();
    }

    onOpenCategory(event) {
      var handle = event && event.detail ? String(event.detail.handle || '') : '';
      if (!handle || !this.tabs || !this.tabs.length) return;

      var suffix = '/collections/' + handle;
      for (var i = 0; i < this.panels.length; i += 1) {
        var url = this.panels[i].collectionUrl.split('?')[0];
        if (url.slice(-suffix.length) === suffix) {
          if (this.activeTab !== i) this.selectTab(i);
          return;
        }
      }
    }

    disconnectedCallback() {
      if (this._onOpenCategory) document.removeEventListener('ld:open-category', this._onOpenCategory);
      if (this.rail && this._onRailClick) this.rail.removeEventListener('click', this._onRailClick);
      if (this.tablist && this._onTabClick) {
        this.tablist.removeEventListener('click', this._onTabClick);
        this.tablist.removeEventListener('keydown', this._onTabKey);
      }
      this.panels.forEach(function (panel) {
        if (panel.moreBtn && panel.onMore) panel.moreBtn.removeEventListener('click', panel.onMore);
        if (panel.controller) {
          try {
            panel.controller.abort();
          } catch (e) {
            /* noop */
          }
        }
      });
      this._initialized = false;
    }

    readPanel(el) {
      return {
        el: el,
        collectionUrl: el.getAttribute('data-collection-url') || '',
        title: el.getAttribute('data-collection-title') || '',
        grid: el.querySelector('[data-ld-panel-grid]'),
        countEl: el.querySelector('[data-ld-panel-count]'),
        emptyEl: el.querySelector('[data-ld-panel-empty]'),
        moreBtn: el.querySelector('[data-ld-panel-more]'),
        noteEl: el.querySelector('[data-ld-panel-note]'),
        /* Seeded from what Liquid painted, so the first Load More is correct
           without a round trip to discover what is already on screen. */
        shown: el.querySelectorAll('.ld-product-card').length,
        /* Seeded by Liquid. The first paint is server-rendered, so without
           these the runtime believes the panel holds nothing and has nowhere
           to go — which is exactly what made Load More inert until a car had
           been picked. */
        total: parseInt(el.getAttribute('data-total'), 10) || 0,
        nextUrl: el.getAttribute('data-next-url') || null,
        token: 0,
        controller: null,
        appendBusy: false,
        onMore: null
      };
    }

    /* -------------------------------------------------------- selection --- */

    onRailClick(event) {
      var chip = event.target.closest('[data-ld-chip]');
      if (!chip || !this.contains(chip)) return;
      if (!isPlainLeftClick(event) || chip.target === '_blank') return;

      event.preventDefault();
      if (chip === this.activeChip) return;
      this.selectChip(chip);
    }

    selectChip(chip, opts) {
      opts = opts || {};
      this.activeChip = chip;
      this.activeExpected = parseTagList(chip.getAttribute('data-tags'));

      this.chips.forEach(function (c) {
        if (c === chip) {
          c.setAttribute('aria-current', 'true');
        } else {
          c.removeAttribute('aria-current');
        }
      });

      var path = chip.getAttribute('data-tag-path') || '';
      var label = chip.getAttribute('data-chip-label') || '';

      if (!opts.silent) {
        this.writeUrl(path);
        pushEvent('labor_day_chassis_select', { chassis: label });
      }

      var self = this;
      this.panels.forEach(function (panel) {
        self.loadPanel(panel, self.panelUrl(panel, path), {
          append: false,
          expected: self.activeExpected
        });
      });
    }

    panelUrl(panel, tagPath) {
      if (!panel.collectionUrl) return '';
      return tagPath ? panel.collectionUrl + '/' + tagPath : panel.collectionUrl;
    }

    /* A campaign page has to be linkable to a pre-filtered state — an email or
       an ad should be able to land on "aero for my 992". Replace rather than
       push, so Back leaves the page instead of walking the rail. */
    writeUrl(tagPath) {
      if (!window.history || typeof window.history.replaceState !== 'function') return;
      try {
        var u = new URL(window.location.href);
        if (tagPath) {
          u.searchParams.set('car', tagPath);
        } else {
          u.searchParams.delete('car');
        }
        window.history.replaceState(window.history.state, '', u.toString());
      } catch (e) {
        /* a URL we cannot rewrite is not worth failing the page over */
      }
    }

    restoreFromUrl() {
      var wanted;
      try {
        wanted = new URL(window.location.href).searchParams.get('car');
      } catch (e) {
        wanted = null;
      }
      if (!wanted) return;

      var match = null;
      this.chips.forEach(function (c) {
        if (!match && (c.getAttribute('data-tag-path') || '') === wanted) match = c;
      });
      /* An unknown ?car= is ignored: the server-rendered all-cars state stays,
         which is the correct answer to a stale link. */
      if (match) this.selectChip(match, { silent: true });
    }

    /* ------------------------------------------------------------ more --- */

    onLoadMore(panel, event) {
      event.preventDefault();
      if (panel.appendBusy || !panel.nextUrl) return;
      panel.hadFocus = document.activeElement === panel.moreBtn;
      panel.prevCards = panel.grid ? panel.grid.children.length : 0;
      this.loadPanel(panel, panel.nextUrl, { append: true, expected: this.activeExpected });
    }

    /* ------------------------------------------------------------ tabs --- */

    onTabClick(event) {
      var tab = event.target.closest('[data-ld-tab]');
      if (!tab || !this.contains(tab)) return;
      event.preventDefault();
      this.selectTab(parseInt(tab.getAttribute('data-tab-index'), 10) || 0);
    }

    /* Arrow keys move between tabs, which is what a tablist is expected to do
       and what a keyboard user will try first. */
    onTabKey(event) {
      var idx = this.tabs.indexOf(document.activeElement);
      if (idx === -1) return;
      var next = null;
      if (event.key === 'ArrowRight') next = (idx + 1) % this.tabs.length;
      else if (event.key === 'ArrowLeft') next = (idx - 1 + this.tabs.length) % this.tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.tabs.length - 1;
      if (next === null) return;
      event.preventDefault();
      this.selectTab(next);
      this.tabs[next].focus();
    }

    selectTab(index, opts) {
      opts = opts || {};
      this.activeTab = index;

      this.tabs.forEach(function (tab, i) {
        var on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
      });

      this.panels.forEach(function (panel, i) {
        panel.el.hidden = i !== index;
      });

      if (!opts.silent) {
        var label = this.tabs[index] ? this.tabs[index].getAttribute('data-tab-name') || '' : '';
        pushEvent('labor_day_category_select', { category: label });
      }
    }

    /* The tab's own count, so a category that is empty for the selected car
       says so before it is opened. */
    updateTabCounts() {
      var self = this;
      this.panels.forEach(function (panel, i) {
        var tab = self.tabs[i];
        if (!tab) return;
        var countEl = tab.querySelector('[data-ld-tab-count]');
        if (countEl) countEl.textContent = panel.total;
        tab.classList.toggle('ld-shop__tab--empty', panel.total === 0);
      });
    }

    /* ----------------------------------------------------------- fetch --- */

    async fetchHtml(panel, requestUrl) {
      if (this.cache.has(requestUrl)) return this.cache.get(requestUrl);

      if (panel.controller) {
        try {
          panel.controller.abort();
        } catch (e) {
          /* noop */
        }
      }
      panel.controller = new AbortController();

      var response = await fetch(requestUrl, {
        signal: panel.controller.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error('HTTP ' + response.status);

      var html = await response.text();
      var payload = { html: html, finalUrl: response.url || requestUrl };
      this.cache.set(requestUrl, payload);
      return payload;
    }

    async loadPanel(panel, rawUrl, opts) {
      opts = opts || {};
      var append = !!opts.append;
      var expected = opts.expected || [];

      if (!rawUrl || !panel.grid) return;

      var requestUrl;
      try {
        requestUrl = withSectionId(rawUrl, this.endpointId);
      } catch (e) {
        return;
      }

      var token = (panel.token += 1);
      this.setPanelBusy(panel, true, append);

      try {
        var payload = await this.fetchHtml(panel, requestUrl);
        if (token !== panel.token) return;

        var doc = new DOMParser().parseFromString(payload.html, 'text/html');
        var root = doc.querySelector('[data-ld-grid-root]');

        if (!root || root.getAttribute('data-error')) {
          if (append) {
            panel.nextUrl = null;
            this.updatePanel(panel);
          } else {
            this.renderPanelEmpty(panel);
          }
          return;
        }

        /* --- Tag-redirect guard, ported verbatim -------------------------
           PRIMARY: the final response URL must still carry every expected
           tag. Shopify's "unknown tag -> redirect to the bare collection"
           drops the tag from the URL, which is exactly what we must catch.
           SECONDARY: the server-reported applied tags only tighten the check
           WHEN PRESENT, so a missing value can never wrongly empty a valid
           filter. If a tag is missing, show the empty state — never the
           unfiltered products. */
        if (expected.length) {
          var applied = parseTagList(root.getAttribute('data-applied-tags'));
          var appliedOk = applied.length === 0 || isSubset(expected, applied);
          var urlOk = true;
          try {
            urlOk = isSubset(
              expected,
              tagsFromPathname(new URL(payload.finalUrl, window.location.origin).pathname)
            );
          } catch (e) {
            urlOk = true;
          }
          if (!appliedOk || !urlOk) {
            panel.nextUrl = null;
            if (append) {
              this.updatePanel(panel);
            } else {
              this.renderPanelEmpty(panel);
            }
            return;
          }
        }

        panel.total = parseInt(root.getAttribute('data-count'), 10) || 0;
        panel.nextUrl = root.getAttribute('data-next-url') || null;

        var incoming = root.querySelector('[data-ld-grid]');
        var cards = incoming
          ? Array.prototype.slice.call(incoming.querySelectorAll('.ld-product-card'))
          : [];

        if (!append && cards.length === 0) {
          this.renderPanelEmpty(panel);
          return;
        }

        if (append) {
          cards.forEach(function (card) {
            panel.grid.appendChild(card);
          });
        } else {
          this.setChildren(panel.grid, cards);
          panel.grid.hidden = false;
          if (panel.emptyEl) panel.emptyEl.hidden = true;
        }

        panel.shown = panel.grid.children.length;
        this.updatePanel(panel);
        if (append) this.restoreFocus(panel);
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        if (token !== panel.token) return;
        if (append) {
          /* Keep the grid and the next URL so the shopper can simply retry the
             same page — never restart them at page one. */
          if (panel.noteEl) panel.noteEl.textContent = this.labels.loadError;
        } else {
          this.renderPanelEmpty(panel);
        }
      } finally {
        /* Unconditional. This is the fix: gating it on the token being current
           is what lets the flag latch forever when a chip click aborts an
           in-flight append. */
        this.setPanelBusy(panel, false, append);
        if (!append) {
          this.updateAnswer();
          if (this.tabs && this.tabs.length) this.updateTabCounts();
        }
      }
    }

    /* ---------------------------------------------------------- render --- */

    setChildren(parent, nodes) {
      if (typeof parent.replaceChildren === 'function') {
        parent.replaceChildren.apply(parent, nodes);
        return;
      }
      while (parent.firstChild) parent.removeChild(parent.firstChild);
      nodes.forEach(function (n) {
        parent.appendChild(n);
      });
    }

    renderPanelEmpty(panel) {
      this.setChildren(panel.grid, []);
      panel.grid.hidden = true;
      panel.shown = 0;
      panel.total = 0;
      panel.nextUrl = null;
      if (panel.emptyEl) panel.emptyEl.hidden = false;
      this.updatePanel(panel);
    }

    updatePanel(panel) {
      if (panel.countEl) {
        panel.countEl.textContent = panel.total + ' ' + this.labels.parts;
      }
      if (panel.moreBtn) {
        var hasMore = !!panel.nextUrl;
        panel.moreBtn.hidden = !hasMore;
        panel.moreBtn.disabled = !hasMore;
      }
      if (panel.noteEl) {
        var left = Math.max(panel.total - panel.shown, 0);
        panel.noteEl.textContent = panel.nextUrl && left > 0 ? left + ' left' : '';
      }
    }

    /* The line that answers the question the visitor actually came with.
       Every figure is a server count; nothing here is typed. */
    updateAnswer() {
      if (!this.answerEl) return;

      var total = 0;
      var stillLoading = false;
      this.panels.forEach(function (p) {
        total += p.total;
        if (p.el.getAttribute('aria-busy') === 'true') stillLoading = true;
      });
      if (stillLoading) return;

      var label = this.activeChip ? this.activeChip.getAttribute('data-chip-label') || '' : '';
      var isAll = !this.activeExpected.length;

      this.answerEl.textContent = isAll
        ? total + ' ' + this.labels.parts + ' ' + this.labels.answerAll
        : this.labels.answerPrefix + ' ' + label + ': ' + total + ' ' + this.labels.parts + ' ' +
          this.labels.answerSuffix;
    }

    restoreFocus(panel) {
      if (!panel.hadFocus) return;
      panel.hadFocus = false;
      if (panel.moreBtn && !panel.moreBtn.hidden) return;
      var cards = panel.grid.children;
      var firstNew = cards[panel.prevCards] || cards[cards.length - 1];
      var target = firstNew && firstNew.querySelector('a[href]');
      if (target && typeof target.focus === 'function') {
        try {
          target.focus({ preventScroll: true });
        } catch (e) {
          target.focus();
        }
      }
    }

    setPanelBusy(panel, isBusy, append) {
      panel.el.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      if (append) {
        panel.appendBusy = !!isBusy;
        if (panel.moreBtn) panel.moreBtn.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      }
    }
  }

  customElements.define('lp-labor-day-shop', LabordayShop);
})();
